"use client"

import { Phone, Mail, MapPin, Clock } from "lucide-react"
import { useState } from "react"
import { useCompanyInfo } from "@/shared/hooks/useCompanyInfo"

export const ContactInformation = () => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)
  const { getByKey, loading } = useCompanyInfo()

  // Fetch dynamic data with fallbacks
  const phoneMobile = getByKey('phone_mobile') || '(+63) 985 590 9000'
  const phoneLandline1 = getByKey('phone_landline_1') || '(+632) 8687 2490'
  const phoneLandline2 = getByKey('phone_landline_2') || '(+632) 8687 2491'
  const email = getByKey('email_primary') || 'info@twi.com.ph'
  const addressLine1 = getByKey('address_line_1') || 'Unit 1007 OMM Citra Bldg. San Miguel Ave. Ortigas Center'
  const addressLine2 = getByKey('address_line_2') || 'Pasig City, Philippines 1600'
  const businessHours = getByKey('business_hours_weekday') || 'Monday - Friday: 8:30 AM - 5:30 PM'

  const contactDetails = [
    {
      icon: Phone,
      title: "Phone",
      details: [phoneMobile, `${phoneLandline1} | ${phoneLandline2}`],
    },
    {
      icon: Mail,
      title: "Email",
      details: [email],
    },
    {
      icon: MapPin,
      title: "Address",
      details: [addressLine1, addressLine2],
    },
    {
      icon: Clock,
      title: "Business Hours",
      details: [businessHours],
    },
  ]

  if (loading) {
    return (
      <section className="py-16 sm:py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-white rounded-xl shadow-lg p-8 animate-pulse">
                <div className="flex flex-col items-center">
                  <div className="w-16 h-16 bg-gray-200 rounded-full mb-5"></div>
                  <div className="h-4 bg-gray-200 rounded w-20 mb-4"></div>
                  <div className="h-3 bg-gray-200 rounded w-32"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    )
  }

  return (
    <section className="py-16 sm:py-20 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-12 animate-in fade-in duration-700">
          <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-700 px-4 py-2 rounded-full text-sm mb-4 font-semibold shadow-sm">
            <span className="w-2 h-2 bg-orange-500 rounded-full animate-pulse"></span>
            Multiple Ways to Connect
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Reach Out to <span className="text-orange-500">Us</span>
          </h2>
          <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
            Choose your preferred method of communication
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {contactDetails.map((item, index) => {
            const Icon = item.icon
            return (
              <div
                key={index}
                className="bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-500 p-6 sm:p-8 border border-gray-100 group animate-in slide-in-from-bottom"
                style={{ animationDelay: `${index * 100}ms` }}
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
              >
                <div className="flex flex-col items-center text-center">
                  <div
                    className="flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 rounded-full mb-5 transition-all duration-500 shadow-md group-hover:shadow-xl group-hover:scale-110"
                    style={{
                      backgroundColor: hoveredIndex === index ? 'rgb(17 24 39)' : 'rgb(249 115 22)'
                    }}
                  >
                    <Icon className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
                  </div>
                  <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-4 uppercase tracking-wide border-b-2 border-orange-500 pb-2">
                    {item.title}
                  </h3>
                  <div className="space-y-2">
                    {item.details.map((detail, idx) => (
                      <p key={idx} className="text-gray-700 text-sm sm:text-base leading-relaxed">
                        {detail}
                      </p>
                    ))}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}
